import { useQuery } from "@tanstack/react-query"
import { Link, Outlet, useLocation, useParams } from "@tanstack/react-router"
import { Archive, ChevronDown, ChevronUp, Ellipsis, Pencil } from "lucide-react"
import { motion } from "motion/react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@atlant/ui"

import { graphql } from "#gql"
import { queryKeys } from "#lib/query-keys.ts"
import { AircraftImageUpload } from "#pages/authenticated/fleet/[aircraftId]/components/aircraft-image-upload.tsx"
import { AssignKamModal } from "#pages/authenticated/fleet/[aircraftId]/components/assign-kam-modal.tsx"
import { AssignOperatorModal } from "#pages/authenticated/fleet/[aircraftId]/components/assign-operator-modal.tsx"
import { AssignOwnerModal } from "#pages/authenticated/fleet/[aircraftId]/components/assign-owner-modal.tsx"
import { useAircraftHeaderStore } from "#pages/authenticated/fleet/[aircraftId]/use-aircraft-header-store.ts"
import { useAircraftSource } from "#pages/authenticated/fleet/[aircraftId]/use-aircraft-source.ts"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { getUserImageUrl } from "#utils/file-url.ts"
import { client } from "#utils/graphql-client.ts"

export const AircraftByIdDocument = graphql(/* GraphQL */ `
  query AircraftById($input: QueryAircraftByIdInput!) {
    aircraftById(input: $input) {
      id
      source
      tail
      manufacturer
      model
      serial
      yom
      yor
      category
      pax
      images
      keyAccountManagers {
        id
        name
        image
      }
      operators {
        id
        source
        name
      }
      owners {
        id
        name
      }
    }
  }
`)

const tabs = [
  { label: "Overview", to: "overview" },
  { label: "Aircraft Info", to: "info" },
  { label: "Flights", to: "flights" },
  { label: "Documents", to: "documents" },
  { label: "Programs", to: "programs" },
  { label: "Expenses Summary", to: "expenses" },
] as const

export const AircraftLayout = () => {
  const { slug, aircraftId } = useParams({
    from: "/_authenticated/$slug/fleet/$aircraftId",
  })
  const { me } = useLoggedInUserInfo()
  const { isExpanded, toggle } = useAircraftHeaderStore()
  const location = useLocation()

  const source = useAircraftSource()

  const { data } = useQuery({
    queryKey: queryKeys.aircraft.detail(aircraftId),
    queryFn: () =>
      client.request(AircraftByIdDocument, {
        input: {
          id: aircraftId,
          organizationId: me.organization.id,
          source,
        },
      }),
  })

  const aircraft = data?.aircraftById ?? null
  const activeTab = tabs.find((tab) => location.pathname.endsWith(`/${tab.to}`))
  const infoLine = aircraft
    ? `S/N ${aircraft.serial} · YOM ${aircraft.yom} · YOR ${aircraft.yor} · ${aircraft.category} · ${aircraft.pax} PAX`
    : "Complete the profile to start managing this aircraft"

  return (
    <div className="flex min-h-full flex-col gap-4 p-4">
      <motion.div layout transition={{ duration: 0.25, ease: "easeInOut" }}>
        <Card className="border-px bg-card text-card-foreground overflow-hidden rounded-2xl p-4 shadow-sm">
          <div className="flex items-start justify-between gap-6">
            <div className="flex w-full flex-col">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">
                  {aircraft?.tail ?? "New Aircraft Profile"}
                </h1>
                {aircraft && (
                  <span className="text-muted-foreground text-sm">
                    {aircraft.manufacturer} {aircraft.model}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground mt-0.5 text-sm">{infoLine}</p>

              <motion.div
                animate={{
                  height: isExpanded ? "auto" : 0,
                  opacity: isExpanded ? 1 : 0,
                }}
                initial={false}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="mt-4 flex flex-col items-start gap-4 sm:flex-row">
                  <AircraftImageUpload
                    source={data?.aircraftById.source || "MANUAL"}
                    images={data?.aircraftById.images || []}
                    aircraftId={aircraftId}
                    yom={data?.aircraftById.yom || ""}
                    model={data?.aircraftById.model || ""}
                    manufacturer={data?.aircraftById.manufacturer || ""}
                    tail={data?.aircraftById.tail || ""}
                    serial={data?.aircraftById.serial || ""}
                  />
                  <div className="flex w-full flex-1 flex-col">
                    <div className="flex items-center gap-8">
                      <div className="flex flex-col text-xs">
                        <span className="text-muted-foreground">KAM</span>
                        <AssignKamModal
                          source={aircraft?.source || "MANUAL"}
                          aircraftId={aircraftId}
                          defaultUserIds={aircraft?.keyAccountManagers.map((k) => k.id)}
                          trigger={
                            aircraft?.keyAccountManagers.length ? (
                              <button
                                type="button"
                                className="group hover:bg-muted -mx-1 mt-1 flex items-center gap-1 rounded-md px-1 py-0.5"
                              >
                                <div className="flex -space-x-2">
                                  {aircraft.keyAccountManagers.map((k) => (
                                    <Avatar
                                      key={k.id}
                                      className="border-background size-7 border-2"
                                    >
                                      <AvatarImage
                                        src={getUserImageUrl(me.organization.id, k.image)}
                                        alt={k.name}
                                      />
                                      <AvatarFallback className="bg-avatar text-xs font-medium text-white">
                                        {k.name
                                          .split(" ")
                                          .map((n) => n[0])
                                          .join("")
                                          .slice(0, 2)
                                          .toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                  ))}
                                </div>
                                <span className="text-foreground truncate text-sm font-medium">
                                  {aircraft.keyAccountManagers
                                    .map((k) => k.name)
                                    .join(", ")}
                                </span>
                                <Pencil className="text-muted-foreground size-3.5 opacity-0 group-hover:opacity-100" />
                              </button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-primary/30 text-primary hover:bg-primary/5 mt-1 h-7"
                              >
                                Assign KAM
                              </Button>
                            )
                          }
                        />
                      </div>

                      <div className="flex flex-col text-xs">
                        <span className="text-muted-foreground">Operator</span>
                        {aircraft?.operators && aircraft.operators.length > 0 ? (
                          <span className="text-foreground mt-1 text-sm font-medium">
                            {aircraft.operators.map((o) => o.name).join(", ")}
                          </span>
                        ) : (
                          <AssignOperatorModal aircraftId={aircraftId} />
                        )}
                      </div>

                      <div className="flex flex-col text-xs">
                        <span className="text-muted-foreground">Owner</span>
                        {aircraft?.owners && aircraft.owners.length > 0 ? (
                          <span className="text-foreground mt-1 text-sm font-medium">
                            {aircraft.owners.map((o) => o.name).join(", ")}
                          </span>
                        ) : (
                          <AssignOwnerModal
                            aircraftId={aircraftId}
                            source={aircraft?.source || "MANUAL"}
                            trigger={
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-primary/30 text-primary hover:bg-primary/5 mt-1 h-7"
                              >
                                Assign Owner
                              </Button>
                            }
                          />
                        )}
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <div className="bg-muted/60 text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium">
                        <span className="bg-muted-foreground/50 size-1.5 rounded-full" />
                        No health data
                      </div>
                      <div className="bg-muted/60 text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium">
                        <span className="bg-muted-foreground/50 size-1.5 rounded-full" />
                        No insurance
                      </div>
                      <div className="bg-muted/60 text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium">
                        <span className="bg-muted-foreground/50 size-1.5 rounded-full" />
                        No registration
                      </div>
                      <div className="bg-muted/60 text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium">
                        <span className="bg-muted-foreground/50 size-1.5 rounded-full" />
                        No program data
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground size-9"
                  >
                    <Ellipsis className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled className="gap-2">
                    <Archive className="size-4" />
                    Send to archive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground size-9"
                onClick={toggle}
              >
                {isExpanded ? (
                  <ChevronUp className="size-4" />
                ) : (
                  <ChevronDown className="size-4" />
                )}
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>

      <div className="flex h-full flex-col">
        <nav className="border-b">
          <div className="flex gap-4 px-2">
            {tabs.map((tab) => {
              const isActive = activeTab?.to === tab.to

              return (
                <Link
                  key={tab.to}
                  to={`/$slug/fleet/$aircraftId/${tab.to}`}
                  params={{ slug, aircraftId }}
                  className="hover:text-foreground text-nav-foreground relative py-2 text-sm font-medium transition-colors"
                  activeProps={{ className: "text-primary" }}
                >
                  {tab.label}
                  {isActive && (
                    <motion.div
                      layoutId={`aircraft-tab-indicator-${isExpanded}`}
                      className="bg-primary absolute right-0 bottom-0 left-0 h-0.5"
                      transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    />
                  )}
                </Link>
              )
            })}
          </div>
        </nav>
        <div className="flex-1 pt-4">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
